import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';

interface WalletContextType {
  publicKey: PublicKey | null;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  connection: Connection;
  signAndSendTransaction: (toPublicKey: string, amount: number) => Promise<string>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [publicKey, setPublicKey] = useState<PublicKey | null>(null);
  const [connected, setConnected] = useState(false);
  //const [connection] = useState(new Connection('https://api.mainnet-beta.solana.com'));
  const [connection] = useState(new Connection('https://mainnet.helius-rpc.com/?api-key=5f380674-0069-4374-8d24-a40bc04d2db3'));

  useEffect(() => {
    // Проверяем, есть ли сохраненный кошелек
    const savedWallet = localStorage.getItem('solana_wallet');
    if (savedWallet) {
      try {
        const pubKey = new PublicKey(savedWallet);
        setPublicKey(pubKey);
        setConnected(true);
      } catch (error) {
        console.error('Invalid saved wallet:', error);
        localStorage.removeItem('solana_wallet');
      }
    }
  }, []);

  const connect = async () => {
    try {
      // Проверяем, доступен ли Phantom
      if (!window.solana || !window.solana.isPhantom) {
        alert('Phantom wallet not found! Please install Phantom wallet.');
        return;
      }

      // Подключаемся к кошельку
      const response = await window.solana.connect();
      const pubKey = new PublicKey(response.publicKey.toString());
      
      setPublicKey(pubKey);
      setConnected(true);
      localStorage.setItem('solana_wallet', pubKey.toString());
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      alert('Failed to connect wallet. Please try again.');
    }
  };

  const disconnect = () => {
    setPublicKey(null);
    setConnected(false);
    localStorage.removeItem('solana_wallet');
  };

  const signAndSendTransaction = async (toPublicKey: string, amount: number): Promise<string> => {
    if (!publicKey || !window.solana) {
      throw new Error('Wallet not connected');
    }

    try {
      // Создаем транзакцию
      const transaction = new Transaction();
      
      // Добавляем инструкцию перевода
      const transferInstruction = SystemProgram.transfer({
        fromPubkey: publicKey,
        toPubkey: new PublicKey(toPublicKey),
        lamports: Math.floor(amount * LAMPORTS_PER_SOL),
      });
      
      transaction.add(transferInstruction);

      // Получаем последний блокхэш
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Подписываем и отправляем транзакцию
      const { signature } = await window.solana.signAndSendTransaction(transaction);
      
      // Ждем подтверждения
      await connection.confirmTransaction(signature, 'confirmed');
      
      return signature;
    } catch (error) {
      console.error('Transaction failed:', error);
      throw error;
    }
  };

  const value: WalletContextType = {
    publicKey,
    connected,
    connect,
    disconnect,
    connection,
    signAndSendTransaction,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Расширяем Window interface для TypeScript
declare global {
  interface Window {
    solana?: {
      isPhantom?: boolean;
      connect: () => Promise<{ publicKey: { toString: () => string } }>;
      disconnect: () => Promise<void>;
      signAndSendTransaction: (transaction: Transaction) => Promise<{ signature: string }>;
    };
  }
}